// Shared building blocks for the dashboard and Super Admin console: cards,
// fields, buttons and switches, all in the In Service Drive style.

export function Toggle({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative h-6 w-[42px] flex-[0_0_42px] cursor-pointer rounded-full border-0 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      style={{ background: checked ? "#2667B5" : "#D0D5DD" }}
    >
      <span
        className="absolute top-[2px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(16,24,40,0.18)] transition-[left] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ left: checked ? 22 : 2 }}
      />
    </button>
  );
}

export function ToggleRow({ title, status, checked, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EAECF0] bg-white px-4 py-[14px]">
      <div>
        <div className="text-[14px] font-medium text-[#111827]">{title}</div>
        {status && <div className="mt-[2px] text-[12px] text-[#6B7280]">{status}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} label={title} disabled={disabled} />
    </div>
  );
}

// Title row of a detail card: heading on the left, its main switch on the
// right, then the divider — the Service journey stage header.
export function DetailHeader({ title, toggleLabel, status, checked, onChange }) {
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-5">
        <h2 className="text-[20px] font-semibold leading-7 text-[#111827]">{title}</h2>
        <div className="flex items-center gap-[10px]">
          {status && <span className="text-[12px] font-medium text-[#B45309]">{status}</span>}
          <span className="text-[13px] font-medium text-[#4B5563]">{toggleLabel}</span>
          <Toggle checked={checked} onChange={onChange} label={toggleLabel} />
        </div>
      </div>
      <div className="my-[22px] h-px bg-[#F0F1F3]" />
    </>
  );
}

// White card on a white page: a hairline border and the faintest lift, the
// way the Chat360 console draws its panels.
export const cardClass = "rounded-xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

export function FieldLabel({ children }) {
  return <label className="mb-[6px] block text-[13px] font-medium text-[#374151]">{children}</label>;
}

export function FieldError({ children }) {
  if (!children) return null;
  return <div className="mt-[6px] text-[12px] text-[#DC2626]">{children}</div>;
}

export const inputClass =
  "w-full h-10 rounded-lg border border-[#D0D5DD] bg-white px-3 text-[14px] text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[border-color,box-shadow] duration-150 placeholder:text-[#98A2B3] hover:border-[#B5BCC7] focus:border-[#2667B5] focus:outline-none focus:shadow-[0_0_0_3px_rgba(38,103,181,0.15)]";

export const textareaClass =
  "w-full rounded-lg border border-[#D0D5DD] bg-white px-3 py-[10px] text-[14px] leading-5 text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[border-color,box-shadow] duration-150 placeholder:text-[#98A2B3] hover:border-[#B5BCC7] focus:border-[#2667B5] focus:outline-none focus:shadow-[0_0_0_3px_rgba(38,103,181,0.15)]";

export function PrimaryButton({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-lg border border-[#2667B5] bg-[#2667B5] px-[14px] text-[14px] font-medium text-white shadow-[0_1px_2px_rgba(16,24,40,0.08)] transition-colors duration-150 hover:border-[#1F5596] hover:bg-[#1F5596] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className="inline-flex h-[38px] cursor-pointer items-center rounded-lg border border-transparent bg-transparent px-3 text-[14px] font-medium text-[#475467] transition-colors duration-150 hover:bg-[#F4F6F9] hover:text-[#101828] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6]"
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-lg border border-[#D0D5DD] bg-white px-[14px] text-[14px] font-medium text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors duration-150 hover:bg-[#F9FAFB] hover:text-[#101828] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function Select({ className = "", children, ...props }) {
  return (
    <div className={`relative ${className}`}>
      <select {...props} className={`${inputClass} cursor-pointer appearance-none pr-9`}>
        {children}
      </select>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" className="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}

export function SegmentButton({ active, children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className="h-[34px] cursor-pointer rounded-lg border px-3 text-[13px] font-medium transition-colors duration-150"
      style={{
        background: active ? "#EFF5FC" : "#FFFFFF",
        borderColor: active ? "#8FB8E6" : "#D0D5DD",
        color: active ? "#1F5596" : "#475467",
      }}
    >
      {children}
    </button>
  );
}


// Page heading for a dashboard page, with optional actions on the right.
export function PageTitle({ title, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-[28px] font-semibold leading-9 text-[#111827]">{title}</h1>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
